/**
 * Shared helpers for reading RAG / legacy analysis fields on candidate resumes.
 */

const UNIFIED_SUMMARY_MARKERS = [
    'unified candidate assessment',
    'overall evaluation:',
    '1. technical assessment',
    'technical hiring recommendation',
    'overall hiring recommendation',
    'actionable insights for the hiring team',
];

/** Parse rag_analysis when stored as a JSON string. */
export function parseRagAnalysis(ragAnalysis) {
    if (!ragAnalysis) {
        return null;
    }
    if (typeof ragAnalysis === 'string') {
        try {
            return JSON.parse(ragAnalysis);
        } catch {
            return null;
        }
    }
    return ragAnalysis;
}

/** Parse analysis when stored as a JSON string. */
export function parseAnalysis(analysis) {
    if (!analysis) {
        return null;
    }
    if (typeof analysis === 'string') {
        try {
            return JSON.parse(analysis);
        } catch {
            return null;
        }
    }
    return analysis;
}

/** Normalized candidate view with parsed analysis / rag_analysis. */
export function normalizeCandidateFields(candidate) {
    if (!candidate) {
        return { analysis: null, ragAnalysis: null, evaluationResult: null };
    }
    const analysis = parseAnalysis(candidate.analysis);
    const ragAnalysis = parseRagAnalysis(candidate.rag_analysis) || candidate.rag_analysis || null;
    const evaluationResult =
        ragAnalysis?.evaluation_result
        || candidate.evaluationResult
        || candidate.evaluation_result
        || null;
    return { analysis, ragAnalysis, evaluationResult };
}

/** Resolve summary_perspectives from analysis or rag_analysis (JobDetailsPage parity). */
export function getSummaryPerspectives(candidate) {
    const { analysis, ragAnalysis } = normalizeCandidateFields(candidate);
    return (
        analysis?.summary_perspectives
        || ragAnalysis?.summary_perspectives
        || null
    );
}

export function isUnifiedAssessmentText(text) {
    if (!text || typeof text !== 'string') {
        return false;
    }
    const lower = text.toLowerCase();
    return UNIFIED_SUMMARY_MARKERS.some((marker) => lower.includes(marker));
}

function uniqueStrings(items) {
    return [...new Set((items || []).filter(Boolean).map((s) => String(s).trim()))];
}

function averageScore(...scores) {
    const valid = scores.filter((s) => s != null && !Number.isNaN(Number(s))).map(Number);
    if (!valid.length) {
        return null;
    }
    return valid.reduce((a, b) => a + b, 0) / valid.length;
}

function hiringRecommendationForScore(score, dimensionLabel, positive = 'proceed') {
    if (score == null || Number.isNaN(Number(score))) {
        return `Further evaluation is recommended to assess ${dimensionLabel.toLowerCase()} before making a hiring decision.`;
    }
    const value = Number(score);
    if (value >= 70) {
        return positive === 'proceed'
            ? `Given the strong ${dimensionLabel.toLowerCase()} profile, the candidate is recommended for progression in the hiring process.`
            : `The candidate demonstrates solid ${dimensionLabel.toLowerCase()} and is recommended for consideration.`;
    }
    if (value >= 50) {
        return `Given mixed ${dimensionLabel.toLowerCase()} signals, additional interview focus on this dimension is recommended before proceeding.`;
    }
    return `Given substantial gaps in ${dimensionLabel.toLowerCase()}, it is recommended that the candidate not be considered for the position at this time.`;
}

function formatStrengthsList(items) {
    const list = uniqueStrings(items);
    return list.length
        ? list.join(', ')
        : 'There is no specific evidence provided to assess positively.';
}

function formatGapsParagraph(gaps, explanation, score) {
    const parts = [];
    const gapList = uniqueStrings(gaps);
    if (gapList.length) {
        parts.push(`The candidate lacks essential skills or experience in ${gapList.join(', ')}.`);
    }
    if (explanation && String(explanation).trim()) {
        parts.push(String(explanation).trim());
    }
    if (score != null && !Number.isNaN(Number(score))) {
        parts.push(`This results in an overall score of ${Number(score).toFixed(1)}/100.`);
    }
    return parts.join(' ') || 'No significant gaps were identified in the evaluation data.';
}

function buildAssessmentSection(title, recLabel, strengths, gaps, explanation, score) {
    return [
        title,
        `Strengths: ${formatStrengthsList(strengths)}`,
        `Gaps: ${formatGapsParagraph(gaps, explanation, score)}`,
        `${recLabel}: ${hiringRecommendationForScore(score, title.replace(/^\d+\.\s*/, '').replace(':', ''))}`,
        '',
    ].join('\n');
}

function buildOverallHiringRecommendation(name, evaluationResult, fitScore) {
    const score =
        evaluationResult?.calibrated_score
        ?? evaluationResult?.fit_score
        ?? evaluationResult?.overall_score
        ?? fitScore;

    const eligibility = evaluationResult?.eligibility_status;
    if (eligibility === 'ineligible') {
        return `${name} does not meet minimum eligibility requirements for this role. Based on the technical, cultural, and strategic evaluations, the candidate should not be considered for this position at this time.`;
    }
    if (score != null && Number(score) < 50) {
        return `Based on the evaluations from the CTO, HR, and CEO perspectives, ${name} is not positioned to meet the technical, cultural, or strategic requirements of the role. The significant gaps in required skills and alignment lead to the recommendation that the candidate should not be considered for this position.`;
    }
    if (score != null && Number(score) >= 70) {
        return `Based on the combined evaluations, ${name} demonstrates strong alignment across technical, cultural, and strategic dimensions and is recommended for progression to the next stage of the hiring process.`;
    }
    return `Based on the combined evaluations, ${name} shows partial alignment with role requirements. Additional interview assessment is recommended before making a final hiring decision.`;
}

function buildActionableInsights(evaluationResult, legacyGaps = []) {
    const gaps = uniqueStrings([
        ...(evaluationResult?.skills_score?.missing_requirements || []),
        ...(evaluationResult?.experience_score?.missing_requirements || []),
        ...(evaluationResult?.education_score?.missing_requirements || []),
        ...legacyGaps,
    ]);
    const strengths = uniqueStrings([
        ...(evaluationResult?.skills_score?.matched_requirements || []),
        ...(evaluationResult?.experience_score?.matched_requirements || []),
    ]);

    const insights = [];
    if (gaps.length) {
        insights.push(`Focus on candidates with proven experience in ${gaps.slice(0, 5).join(', ')}.`);
    }
    if (strengths.length) {
        insights.push(`Validate demonstrated strengths in ${strengths.slice(0, 4).join(', ')} during structured interviews.`);
    }
    insights.push('Assess collaboration and communication skills through targeted behavioral interview questions.');
    insights.push('Evaluate strategic thinking and business impact during the interview process to ensure alignment with organizational goals.');

    return insights.map((line) => `- ${line}`).join('\n');
}

function buildInSummaryParagraph(name, evaluationResult, fitScore, legacyStrengths = [], legacyGaps = []) {
    const strengths = uniqueStrings([
        ...(evaluationResult?.skills_score?.matched_requirements || []),
        ...(legacyStrengths),
    ]);
    const gaps = uniqueStrings([
        ...(evaluationResult?.skills_score?.missing_requirements || []),
        ...(evaluationResult?.experience_score?.missing_requirements || []),
        ...legacyGaps,
    ]);
    const score =
        evaluationResult?.calibrated_score
        ?? evaluationResult?.fit_score
        ?? fitScore;

    let summary = `In summary, ${name} has been evaluated across technical, cultural, and strategic dimensions.`;
    if (strengths.length) {
        summary += ` Strengths include ${strengths.slice(0, 4).join(', ')}.`;
    }
    if (gaps.length) {
        summary += ` Critical gaps remain in ${gaps.slice(0, 4).join(', ')}.`;
    }
    if (score != null && Number(score) < 50) {
        summary += ' These gaps indicate the candidate would not be able to contribute effectively to the team or organization in this role.';
    } else if (score != null && Number(score) >= 70) {
        summary += ' Overall, the candidate appears well-positioned to contribute effectively in this role.';
    } else {
        summary += ' Further assessment is advised before a final hiring decision.';
    }
    return summary;
}

function buildAaravFormatHeader(name, overallText) {
    return [
        `Unified Candidate Assessment Summary for ${name}`,
        '',
        'Overall Evaluation:',
        overallText,
        '',
    ].join('\n');
}

/**
 * Build full Aarav-format summary from CTO/HR/CEO perspectives when combined_summary is missing.
 */
export function buildCombinedSummaryFromPerspectives(candidate, perspectives, evaluationResult) {
    const cto = perspectives?.cto_perspective;
    const hr = perspectives?.hr_perspective;
    const ceo = perspectives?.ceo_perspective;
    if (!cto && !hr && !ceo) {
        return null;
    }

    const name =
        candidate?.candidate_name
        || evaluationResult?.candidate_name
        || 'Candidate';

    const overallText =
        evaluationResult?.summary
        || `${name} has been assessed across three key dimensions: technical skills, cultural fit, and strategic alignment. The evaluations below synthesize CTO, HR, and CEO perspectives for this role.`;

    const sections = [buildAaravFormatHeader(name, overallText)];

    if (cto) {
        sections.push('1. Technical Assessment:', String(cto).trim(), '');
    }
    if (hr) {
        sections.push('2. Cultural Fit Assessment:', String(hr).trim(), '');
    }
    if (ceo) {
        sections.push('3. Strategic Assessment:', String(ceo).trim(), '');
    }

    sections.push(
        'Overall Hiring Recommendation:',
        buildOverallHiringRecommendation(name, evaluationResult, candidate?.analysis?.fit_score),
        '',
        'Actionable Insights for the Hiring Team:',
        buildActionableInsights(evaluationResult),
        '',
        buildInSummaryParagraph(name, evaluationResult, candidate?.analysis?.fit_score),
    );

    const text = sections.join('\n').trim();
    return text.length > 200 ? text : null;
}

/**
 * Build Aarav-format structured summary from evaluation_result dimension scores.
 */
export function buildStructuredDecisionSummary(candidate, evaluationResult) {
    if (!evaluationResult?.skills_score && !evaluationResult?.experience_score) {
        return null;
    }

    const name =
        candidate?.candidate_name
        || evaluationResult.candidate_name
        || 'Candidate';

    const skills = evaluationResult.skills_score || {};
    const experience = evaluationResult.experience_score || {};
    const education = evaluationResult.education_score || {};
    const projects = evaluationResult.projects_score || {};
    const leadership = evaluationResult.leadership_score || {};

    const techStrengths = uniqueStrings([
        ...(skills.matched_requirements || skills.strengths || []),
        ...(experience.matched_requirements || experience.strengths || []),
    ]);
    const techGaps = uniqueStrings([
        ...(skills.missing_requirements || skills.gaps || []),
        ...(experience.missing_requirements || experience.gaps || []),
    ]);
    const techExplanation = [skills.explanation, experience.explanation, projects.explanation]
        .filter(Boolean)
        .join(' ');
    const techScore = averageScore(skills.score, experience.score);

    const culturalStrengths = uniqueStrings([
        ...(education.matched_requirements || []),
    ]);
    const culturalGaps = uniqueStrings([
        ...(education.missing_requirements || []),
    ]);
    const culturalExplanation =
        education.explanation
        || 'The evaluation data focuses primarily on technical skills and provides limited direct evidence for cultural fit, collaboration, and communication.';
    const culturalScore = evaluationResult.fit_score ?? averageScore(education.score);

    const strategicStrengths = uniqueStrings([
        ...(leadership.matched_requirements || leadership.strengths || []),
        ...(projects.matched_requirements || projects.strengths || []),
    ]);
    const strategicGaps = uniqueStrings([
        ...(leadership.missing_requirements || leadership.gaps || []),
        ...(projects.missing_requirements || projects.gaps || []),
    ]);
    const strategicExplanation =
        [leadership.explanation, projects.explanation].filter(Boolean).join(' ')
        || 'The evaluation data does not provide sufficient information to assess strategic thinking, business impact, or market positioning.';
    const strategicScore = averageScore(leadership.score, projects.score);

    const overallText =
        evaluationResult.summary
        && !isUnifiedAssessmentText(evaluationResult.summary)
            ? evaluationResult.summary
            : `${name} has been assessed across three key dimensions: technical skills, cultural fit, and strategic alignment. The evaluations indicate ${techScore != null && techScore < 50 ? 'significant gaps' : 'mixed signals'} in technical competencies${culturalScore != null && culturalScore < 50 ? ' and cultural alignment' : ''} relative to the role requirements.`;

    const sections = [
        buildAaravFormatHeader(name, overallText),
        buildAssessmentSection(
            '1. Technical Assessment:',
            'Technical Hiring Recommendation',
            techStrengths,
            techGaps,
            techExplanation,
            techScore,
        ),
        buildAssessmentSection(
            '2. Cultural Fit Assessment:',
            'Cultural Fit Hiring Recommendation',
            culturalStrengths,
            culturalGaps,
            culturalExplanation,
            culturalScore,
        ),
        buildAssessmentSection(
            '3. Strategic Assessment:',
            'Strategic Hiring Recommendation',
            strategicStrengths,
            strategicGaps,
            strategicExplanation,
            strategicScore,
        ),
        'Overall Hiring Recommendation:',
        buildOverallHiringRecommendation(name, evaluationResult),
        '',
        'Actionable Insights for the Hiring Team:',
        buildActionableInsights(evaluationResult),
        '',
        buildInSummaryParagraph(name, evaluationResult),
    ];

    const text = sections.join('\n').trim();
    return text.length > 200 ? text : null;
}

function extractSectionComment(block) {
    if (!block) {
        return null;
    }
    if (typeof block === 'string') {
        return block.trim() || null;
    }
    if (typeof block === 'object') {
        const text = block.comment || block.overall_comment;
        if (text && String(text).trim()) {
            return String(text).trim();
        }
        if (block.scores && typeof block.scores === 'object') {
            const scoreLines = Object.entries(block.scores)
                .map(([key, value]) => `- ${key.replace(/_/g, ' ')}: ${value}`)
                .join('\n');
            return scoreLines || null;
        }
    }
    return null;
}

/**
 * Build Aarav-format summary from legacy advanced_analysis (pre-RAG resumes).
 */
export function buildLegacyStructuredSummary(candidate, analysis) {
    const adv = analysis?.advanced_analysis;
    if (!adv) {
        return null;
    }

    const detailed =
        adv.filtered_detailed_reasoning
        || adv.detailed_reasoning
        || null;
    const overallAssessment =
        adv.filtered_overall_assessment
        || adv.overall_assessment
        || null;

    if (!detailed && !overallAssessment) {
        return null;
    }

    const name =
        candidate?.candidate_name
        || analysis?.candidate_name
        || 'Candidate';

    const strengths = overallAssessment?.strengths || [];
    const gaps = overallAssessment?.Shortfall_Areas || overallAssessment?.shortfall_areas || [];

    const skillsComment = extractSectionComment(detailed?.skills);
    const experienceComment = extractSectionComment(detailed?.experience);
    const educationComment = extractSectionComment(detailed?.education);
    const techExplanation = [skillsComment, experienceComment].filter(Boolean).join(' ');
    const fitScore = analysis?.fit_score;

    const overallText =
        analysis?.reasoning && !isUnifiedAssessmentText(analysis.reasoning)
            ? String(analysis.reasoning).trim()
            : `${name} has been assessed across technical skills, experience, and education dimensions. The evaluation highlights both relevant strengths and critical gaps relative to the role requirements.`;

    const sections = [
        buildAaravFormatHeader(name, overallText),
        buildAssessmentSection(
            '1. Technical Assessment:',
            'Technical Hiring Recommendation',
            strengths,
            gaps,
            techExplanation,
            fitScore != null ? Number(fitScore) : null,
        ),
        buildAssessmentSection(
            '2. Cultural Fit Assessment:',
            'Cultural Fit Hiring Recommendation',
            [],
            [],
            'The legacy evaluation focuses primarily on technical and experience dimensions. Collaboration, teamwork, and communication should be validated through behavioral interview questions.',
            null,
        ),
        buildAssessmentSection(
            '3. Strategic Assessment:',
            'Strategic Hiring Recommendation',
            [],
            [],
            educationComment || 'The evaluation data does not provide sufficient information to assess strategic thinking, business impact, or market positioning.',
            null,
        ),
        'Overall Hiring Recommendation:',
        buildOverallHiringRecommendation(name, null, fitScore),
        '',
        'Actionable Insights for the Hiring Team:',
        buildActionableInsights(null, gaps),
        '',
        buildInSummaryParagraph(name, null, fitScore, strengths, gaps),
    ];

    const text = sections.join('\n').trim();
    return text.length > 200 ? text : null;
}

function pickLongestText(...candidates) {
    const valid = candidates.filter((t) => t && String(t).trim());
    if (!valid.length) {
        return null;
    }
    return valid.sort((a, b) => String(b).length - String(a).length)[0].trim();
}

/**
 * Decision Summary text for the Summary tab — prefer full unified assessment (Aarav-style).
 */
export function getDecisionSummaryText(candidate) {
    if (!candidate) {
        return null;
    }

    const { analysis, evaluationResult } = normalizeCandidateFields(candidate);
    const perspectives = getSummaryPerspectives(candidate);

    const combinedSummary = perspectives?.combined_summary;
    if (combinedSummary && String(combinedSummary).trim()) {
        return String(combinedSummary).trim();
    }

    const reasoning = analysis?.reasoning;
    if (reasoning && isUnifiedAssessmentText(reasoning)) {
        return String(reasoning).trim();
    }

    const fromPerspectives = buildCombinedSummaryFromPerspectives(candidate, perspectives, evaluationResult);
    if (fromPerspectives) {
        return fromPerspectives;
    }

    const structured = buildStructuredDecisionSummary(candidate, evaluationResult);
    if (structured) {
        return structured;
    }

    const legacyStructured = buildLegacyStructuredSummary(candidate, analysis);
    if (legacyStructured) {
        return legacyStructured;
    }

    if (perspectives?.cto_perspective || perspectives?.hr_perspective || perspectives?.ceo_perspective) {
        const stitched = buildCombinedSummaryFromPerspectives(candidate, perspectives, evaluationResult);
        if (stitched) {
            return stitched;
        }
    }

    const evalSummary = evaluationResult?.summary;
    return pickLongestText(reasoning, evalSummary);
}

const EMAIL_PLACEHOLDERS = new Set(['no email found', 'none', 'n/a', 'null', '']);

/** True when value looks like a real email address (not extraction placeholders). */
export function isUsableCandidateEmail(value) {
    if (value == null) return false;
    const s = String(value).trim();
    if (!s || EMAIL_PLACEHOLDERS.has(s.toLowerCase())) return false;
    const at = s.indexOf('@');
    if (at < 1) return false;
    const domain = s.slice(at + 1);
    return domain.includes('.');
}

function emailFromObject(obj) {
    if (!obj || typeof obj !== 'object') return null;
    for (const key of ['email', 'candidate_email', 'Email']) {
        if (isUsableCandidateEmail(obj[key])) return String(obj[key]).trim();
    }
    if (obj.logistics && isUsableCandidateEmail(obj.logistics.email)) {
        return String(obj.logistics.email).trim();
    }
    const contact = obj.contact || obj.contact_info;
    if (contact && isUsableCandidateEmail(contact.email)) {
        return String(contact.email).trim();
    }
    return null;
}

function emailFromText(text) {
    if (!text || typeof text !== 'string') return null;
    const m = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
    return m && isUsableCandidateEmail(m[0]) ? m[0] : null;
}

function emailFromApplicationAnswers(answers) {
    if (!answers || typeof answers !== 'object') {
        return null;
    }
    for (const key of ['_applicant_email', 'applicant_email', 'candidate_email']) {
        if (isUsableCandidateEmail(answers[key])) {
            return String(answers[key]).trim();
        }
    }
    return null;
}

/**
 * Resolve display name + email for a resume/candidate object from column + analysis JSON.
 * Application-form email wins over resume-scraped analysis/content.
 */
export function resolveCandidateContact(candidate) {
    if (!candidate) {
        return { name: 'Candidate', email: null };
    }

    let email = emailFromApplicationAnswers(candidate.application_answers);
    if (!email) {
        email = isUsableCandidateEmail(candidate.email) ? String(candidate.email).trim() : null;
    }
    const analysis = parseAnalysis(candidate.analysis);
    const rag = parseRagAnalysis(candidate.rag_analysis);

    if (!email) {
        email =
            emailFromObject(analysis) ||
            emailFromObject(rag) ||
            emailFromObject(analysis?.evaluation_result) ||
            emailFromObject(rag?.evaluation_result) ||
            emailFromObject(analysis?.agent_metadata) ||
            emailFromText(candidate.content) ||
            null;
    }

    const looksLikeFilename = (s) =>
        /\.(pdf|docx?|txt|rtf)$/i.test(s) || /resume|cv[_-]/i.test(s);

    let name =
        (analysis?.candidate_name && String(analysis.candidate_name).trim()) ||
        (rag?.evaluation_result?.candidate_name && String(rag.evaluation_result.candidate_name).trim()) ||
        (candidate.candidate_name && String(candidate.candidate_name).trim()) ||
        '';

    if (!name || looksLikeFilename(name) || name.toLowerCase() === 'name not found') {
        name = candidate.filename
            ? String(candidate.filename)
                  .replace(/\.(pdf|docx?|txt|rtf)$/i, '')
                  .replace(/[_-]+/g, ' ')
                  .trim()
            : 'Candidate';
    }

    return { name: name || 'Candidate', email };
}
